import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { KeyRound, Laptop, LogOut, ShieldCheck } from "lucide-react";
import { PASSWORD_MIN } from "../../shared/contracts.js";
import { api, errorMessage, isApiError, type SessionInfo } from "../api.js";
import { useLogout, useMe } from "../components/AppShell.js";
import { PasswordInput, PasswordStrength } from "../components/PasswordFields.js";
import { Badge } from "../components/ui/Badge.js";
import { Button } from "../components/ui/Button.js";
import { Card, CardBody, CardHeader } from "../components/ui/Card.js";
import { ConfirmDialog } from "../components/ui/Dialog.js";
import { Field } from "../components/ui/Form.js";
import { ErrorState, LoadingBlock } from "../components/ui/States.js";
import { useToast } from "../components/ui/Toast.js";
import { fmtDateTime, fmtRelative } from "../lib/format.js";
import { useDocumentTitle } from "../lib/hooks.js";
import { describeUserAgent, ROLE_LABELS } from "../lib/labels.js";
import { assessPassword } from "../lib/password.js";
import { qk } from "../queries.js";

export function AccountPage() {
  useDocumentTitle("Mi cuenta");
  const me = useMe();
  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-fg">Mi cuenta</h1>
        <p className="text-muted">
          {me.user.fullName} · {me.user.email} · {ROLE_LABELS[me.user.role]}
          {me.institution ? ` · ${me.institution.name}` : ""}
        </p>
        <p className="mt-1 text-[12.5px] text-subtle">
          La sesión se cierra sola por inactividad ({fmtDateTime(me.session.idleExpiresAt)}) y como máximo el{" "}
          {fmtDateTime(me.session.absoluteExpiresAt)}.
        </p>
      </div>
      <ChangePasswordCard />
      <SessionsCard />
    </div>
  );
}

function ChangePasswordCard() {
  const me = useMe();
  const toast = useToast();
  const qc = useQueryClient();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [errors, setErrors] = useState<{ current?: string; next?: string; confirm?: string; form?: string }>({});

  const change = useMutation({
    mutationFn: api.auth.changePassword,
    onSuccess: () => {
      setCurrent("");
      setNext("");
      setConfirm("");
      setErrors({});
      toast.success("Contraseña actualizada. Las demás sesiones abiertas se cerraron.");
      void qc.invalidateQueries({ queryKey: qk.sessions });
    },
    onError: (err) => {
      if (isApiError(err, 400)) {
        const fe = err.fieldErrors;
        // Con un solo error, el título del problema es la explicación completa.
        const single = (err.problem.errors?.length ?? 0) === 1;
        if (fe.currentPassword) setErrors({ current: "La contraseña actual no es correcta." });
        else if (fe.newPassword) setErrors({ next: single ? err.problem.title : fe.newPassword });
        else setErrors({ form: errorMessage(err) });
      } else {
        setErrors({ form: errorMessage(err) });
      }
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const a = assessPassword(next, { email: me.user.email, fullName: me.user.fullName });
    const errs: typeof errors = {};
    if (!current) errs.current = "Ingresa tu contraseña actual.";
    if (!a.valid) errs.next = a.hints[0] ?? `Usa al menos ${PASSWORD_MIN} caracteres.`;
    else if (next === current) errs.next = "La nueva contraseña debe ser distinta de la actual.";
    if (confirm !== next) errs.confirm = "Las contraseñas no coinciden.";
    setErrors(errs);
    if (Object.keys(errs).length === 0) change.mutate({ currentPassword: current, newPassword: next });
  }

  return (
    <Card>
      <CardHeader title="Cambiar contraseña" description="Al cambiarla se cierran tus otras sesiones abiertas." />
      <CardBody>
        <form onSubmit={onSubmit} noValidate className="grid max-w-md gap-4">
          <input type="email" name="username" autoComplete="username" value={me.user.email} readOnly hidden />
          <Field label="Contraseña actual" required error={errors.current}>
            {(p) => (
              <PasswordInput {...p} autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} maxLength={1024} />
            )}
          </Field>
          <Field label="Nueva contraseña" required error={errors.next}>
            {(p) => (
              <PasswordInput {...p} autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} maxLength={256} />
            )}
          </Field>
          <PasswordStrength password={next} email={me.user.email} fullName={me.user.fullName} />
          <Field label="Repite la nueva contraseña" required error={errors.confirm}>
            {(p) => (
              <PasswordInput {...p} autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} maxLength={256} />
            )}
          </Field>
          {errors.form && (
            <p role="alert" className="text-[13px] font-medium text-danger">
              {errors.form}
            </p>
          )}
          <div>
            <Button type="submit" variant="primary" loading={change.isPending} icon={<KeyRound className="size-4" aria-hidden="true" />}>
              Cambiar contraseña
            </Button>
          </div>
        </form>
      </CardBody>
    </Card>
  );
}

function SessionsCard() {
  const qc = useQueryClient();
  const toast = useToast();
  const logout = useLogout();
  const sessions = useQuery({ queryKey: qk.sessions, queryFn: ({ signal }) => api.auth.sessions(signal) });
  const [target, setTarget] = useState<SessionInfo | null>(null);

  const revoke = useMutation({
    mutationFn: (id: string) => api.auth.revokeSession(id),
    onSuccess: () => {
      setTarget(null);
      toast.success("Sesión cerrada.");
      void qc.invalidateQueries({ queryKey: qk.sessions });
    },
  });

  const items = [...(sessions.data?.items ?? [])].sort(
    (a, b) => Number(b.current) - Number(a.current) || b.lastSeenAt.localeCompare(a.lastSeenAt),
  );

  return (
    <Card>
      <CardHeader
        title="Sesiones activas"
        description="Si no reconoces una sesión, ciérrala y cambia tu contraseña."
      />
      {sessions.isPending ? (
        <LoadingBlock />
      ) : sessions.isError ? (
        <ErrorState error={sessions.error} onRetry={() => void sessions.refetch()} className="m-4" />
      ) : (
        <ul className="divide-y divide-border">
          {items.map((s) => (
            <li key={s.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
              <Laptop className="size-5 shrink-0 text-subtle" aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <p className="flex flex-wrap items-center gap-2 font-medium text-fg">
                  {describeUserAgent(s.userAgent)}
                  {s.current && (
                    <Badge tone="success" icon={<ShieldCheck />}>
                      Esta sesión
                    </Badge>
                  )}
                </p>
                <p className="text-[12.5px] text-muted">
                  {s.ip ? `IP ${s.ip} · ` : ""}iniciada el {fmtDateTime(s.createdAt)} · última actividad {fmtRelative(s.lastSeenAt)}
                </p>
              </div>
              {s.current ? (
                <Button size="sm" onClick={() => logout.mutate()} loading={logout.isPending} icon={<LogOut className="size-4" aria-hidden="true" />}>
                  Cerrar sesión
                </Button>
              ) : (
                <Button size="sm" variant="ghost" className="text-danger" onClick={() => setTarget(s)}>
                  Cerrar
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      <ConfirmDialog
        open={target !== null}
        onClose={() => {
          setTarget(null);
          revoke.reset();
        }}
        onConfirm={() => target && revoke.mutate(target.id)}
        title="Cerrar sesión remota"
        confirmLabel="Cerrar sesión"
        danger
        pending={revoke.isPending}
        error={revoke.isError ? errorMessage(revoke.error) : null}
      >
        <p>
          Se cerrará la sesión de {describeUserAgent(target?.userAgent ?? null)}
          {target?.ip ? ` (IP ${target.ip})` : ""}. Quien la esté usando tendrá que volver a ingresar.
        </p>
      </ConfirmDialog>
    </Card>
  );
}
